import React, {
  InputHTMLAttributes,
  MouseEvent,
  ReactNode,
  useState,
} from 'react';
import MuiInput, { InputProps } from '@mui/material/Input';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { styled } from '@mui/material/styles';
import { Box, IconButton, Typography } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import searchIcon from '@assets/images/searchIcon.svg';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export interface CustomInputProps extends InputProps {
  label?: string;
  inputStyle?: InputHTMLAttributes<HTMLInputElement>['style'];
  labelStyle?: InputHTMLAttributes<HTMLInputElement>['style'];
  errorMessage?: string;
  warningMessage?: string | undefined | null;
  showErrorMsg?: boolean;
  helperText?: string | null;
  icon?: ReactNode;
  value?: string;
  isSearch?: boolean;
  isPassword?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  clearAction?: () => void;
  keyword?: string;
}

const StyledFormControl = styled(FormControl)(() => ({
  width: '100%',
}));

const CustomLabel = styled(Typography)(({ theme }) => ({
  color:
    theme.palette.mode === 'light' ? 'var(--gray-700)' : 'var(--neutral-200)',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1px',
  marginBottom: '6px',

  transition: theme.transitions.create(['color']),
  '&.Mui-disabled': {
    borderColor: 'var(--neutral-700)',
  },
  '&.Mui-focused': {
    borderColor: 'var(--purple-600)',
  },
}));

const CustomInput = styled(MuiInput)(({ theme }) => ({
  borderRadius: '6px',
  height: '42px',
  width: '100%',
  position: 'relative',
  backgroundColor:
    theme.palette.mode === 'light' ? 'var(--base-white)' : 'var(--gray-600)',
  border:
    theme.palette.mode === 'light'
      ? '1px solid var(--neutral-700)'
      : '1px solid #2E3545',
  color:
    theme.palette.mode === 'light' ? 'var(--gray-700)' : 'var(--neutral-200)',
  boxShadow: `var(--shadow-xsm)`,
  fontSize: 15,
  padding: '10px 16px',
  textAlign: 'left',
  transition: theme.transitions.create(['border-color']),
  '&.Mui-disabled': {
    borderColor: 'var(--neutral-700)',
    backgroundColor: 'var(--neutral-200)',
  },
  '&.Mui-focused': {
    borderColor: 'var(--neutral-900)',
  },
}));

const Input = React.forwardRef<HTMLInputElement, CustomInputProps>(
  (props, ref) => {
    const {
      style,
      label,
      inputStyle,
      labelStyle,
      errorMessage,
      warningMessage,
      icon,
      showErrorMsg = false,
      helperText = '',
      value,
      isSearch,
      isPassword,
      startAdornment,
      endAdornment,
      clearAction,
      keyword,
      ...otherProps
    } = props;
    const [showPassword, setShowPassword] = useState(false);
    const hasError = Boolean(errorMessage);
    const errorStyle = hasError
      ? { border: '1px solid var(--danger-500)' }
      : null;

    const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    };

    return (
      <Box style={style}>
        {label && <CustomLabel style={labelStyle}>{label}</CustomLabel>}
        <StyledFormControl
          error={hasError}
          variant="standard"
          sx={{ gap: '6px' }}
        >
          <CustomInput
            ref={ref}
            sx={errorStyle}
            style={inputStyle}
            {...otherProps}
            disableUnderline
            type={(isPassword && !showPassword && 'password') || 'text'}
            value={value}
            startAdornment={
              isSearch ? (
                <img
                  src={searchIcon}
                  alt="search"
                  width={20}
                  height={20}
                  style={{ marginRight: '8px' }}
                />
              ) : (
                startAdornment
              )
            }
            endAdornment={
              (keyword && keyword.length > 0 && (
                <IconButton
                  onClick={clearAction}
                  sx={{ padding: '4px' }}
                  title="Clear"
                >
                  <CloseIcon sx={{ width: '20px', height: '20px' }} />
                </IconButton>
              )) ||
              (isPassword && (
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? (
                    <Visibility sx={{ color: 'var(--gray-25)' }} />
                  ) : (
                    <VisibilityOff sx={{ color: 'var(--gray-25)' }} />
                  )}
                </IconButton>
              )) ||
              endAdornment
            }
          />

          {/* {helperText && (
            <FormHelperText
              sx={{
                width: '100%',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '20px',
                letterSpacing: '-0.1px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'var(--gray-50)',
              }}
            >
              {helperText}
            </FormHelperText>
          )} */}
          {showErrorMsg && (
            <FormHelperText
              sx={{
                width: '100%',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                letterSpacing: '-0.1px',
                color: hasError ? 'var(--danger-500)' : 'var(--gray-50)',
                margin: 'unset',
              }}
            >
              {hasError ? errorMessage : warningMessage || ' '}
            </FormHelperText>
          )}
        </StyledFormControl>
      </Box>
    );
  },
);

export default Input;
